import React from 'react';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import ScheduleIcon from '@mui/icons-material/Schedule';

import { useGetWeatherQuery } from '../api/apiSlice';
import { weatherIcon, weatherCondition } from '../constants/weatherCodes';

interface ActivityWeatherSlideProps {
    lat: string;
    lng: string;
    /** The outing's START_TIME, e.g. "2026-09-06 18:00:00". */
    startTime: string;
}

const round = (n: number | null | undefined) => (n == null ? null : Math.round(n));

function ActivityWeatherSlide({ lat, lng, startTime }: ActivityWeatherSlideProps) {
    const { t, i18n } = useTranslation();
    const date = startTime.slice(0, 10);
    const startHour = Number(startTime.slice(11, 13));

    const { data, isLoading, isError } = useGetWeatherQuery({ lat, lon: lng, date });

    const shell = (children: React.ReactNode) => (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                p: '14px 16px',
                color: '#0e2438',
                background: 'linear-gradient(165deg, #5b93c7, #86b4d8 45%, #cfe0ee)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
            }}
        >
            {children}
        </Box>
    );

    if (isLoading) {
        return shell(<CircularProgress size={22} sx={{ color: '#0e2438', alignSelf: 'center' }} />);
    }

    if (isError || !data || !data.available) {
        return shell(
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, alignSelf: 'center', opacity: 0.85 }}>
                <ScheduleIcon sx={{ fontSize: 18 }} />
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 500 }}>{t('weather.unavailable')}</Typography>
            </Box>,
        );
    }

    const Icon = weatherIcon(data.weatherCode);
    const conditionLabel = t(`weather.${weatherCondition(data.weatherCode)}`);

    // Three hourly readings bracketing the start time.
    const near = data.hourly
        .map((h) => ({ ...h, hour: Number(h.time.slice(11, 13)) }))
        .filter((h) => h.hour >= startHour - 1 && h.hour <= startHour + 2)
        .slice(0, 4);

    const startReading = data.hourly.find((h) => Number(h.time.slice(11, 13)) === startHour);
    const headlineTemp = round(startReading?.temp) ?? round(data.tempMax);

    const timeFmt = new Intl.DateTimeFormat(i18n.language === 'fr' ? 'fr-FR' : 'en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
    });

    return shell(
        <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                    <Typography sx={{ fontSize: '2.4rem', fontWeight: 700, lineHeight: 1, letterSpacing: '-0.02em' }}>
                        {headlineTemp != null ? `${headlineTemp}°` : '—'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.84rem', fontWeight: 500, opacity: 0.9, mt: 0.25 }}>
                        {conditionLabel}
                        {data.precipProbMax != null ? ` · ${t('weather.precip', { value: data.precipProbMax })}` : ''}
                    </Typography>
                </Box>
                <Icon sx={{ fontSize: 42, color: '#fdf3d3' }} />
            </Box>

            {near.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1.75, mt: 1.5, fontFamily: 'monospace', fontSize: '0.66rem', opacity: 0.92 }}>
                    {near.map((h) => (
                        <Box key={h.time} sx={{ textAlign: 'left' }}>
                            <Box component="span" sx={{ display: 'block', fontSize: '0.78rem', fontWeight: 500 }}>
                                {round(h.temp) != null ? `${round(h.temp)}°` : '—'}
                            </Box>
                            {h.time.slice(11, 16)}
                        </Box>
                    ))}
                </Box>
            )}

            <Typography sx={{ mt: 1.25, fontFamily: 'monospace', fontSize: '0.62rem', opacity: 0.8 }}>
                {t('weather.forecastFor', { when: timeFmt.format(new Date(startTime.replace(' ', 'T'))) })}
            </Typography>
        </>,
    );
}

export default ActivityWeatherSlide;
